import { createAsyncThunk } from '@reduxjs/toolkit'
import { goalsAPI } from '../../utils/supabase'

// Generate temporary ID for optimistic updates
const generateTempId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

// Quarterly Goals Thunks
export const fetchQuarterlyGoals = createAsyncThunk(
  'goals/fetchQuarterlyGoals',
  async (userId, { rejectWithValue }) => {
    try {
      const { data, error } = await goalsAPI.getAllQuarterlyGoals(userId)
      if (error) throw error
      return data
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createQuarterlyGoal = createAsyncThunk(
  'goals/createQuarterlyGoal',
  async ({ userId, goalData }, { rejectWithValue, getState }) => {
    const tempId = generateTempId()
    
    try {
      const { data, error } = await goalsAPI.createQuarterlyGoal(userId, goalData)
      if (error) throw error
      return { 
        data, 
        tempId,
        type: 'create'
      }
    } catch (error) {
      return rejectWithValue({ 
        message: error.message, 
        tempId,
        type: 'create'
      })
    }
  }
)

export const updateQuarterlyGoal = createAsyncThunk(
  'goals/updateQuarterlyGoal',
  async ({ id, ...updates }, { rejectWithValue, getState }) => {
    const state = getState()
    const originalGoal = state.goals.quarterlyGoals.find(goal => goal.id === id)

    // Detect if this is a reschedule operation (quarter or year changed)
    // Only triggers cascade if the goal was already scheduled (has quarter and year)
    const isReschedule = originalGoal &&
      originalGoal.status === 'scheduled' &&
      originalGoal.quarter !== null &&
      originalGoal.year !== null &&
      (updates.quarter !== undefined || updates.year !== undefined) &&
      (updates.quarter !== originalGoal.quarter || updates.year !== originalGoal.year)

    try {
      let data, weeklyGoals = []

      if (isReschedule) {
        // Use cascade reschedule RPC to update quarterly goal and all child weekly goals
        const { quarter, year, ...otherUpdates } = updates
        const result = await goalsAPI.cascadeRescheduleQuarterlyGoal(
          id,
          quarter ?? originalGoal.quarter,
          year ?? originalGoal.year,
          otherUpdates
        )
        if (result.error) throw result.error
        data = result.data.quarterlyGoal
        weeklyGoals = result.data.weeklyGoals
      } else {
        // Regular update without cascade
        const result = await goalsAPI.updateQuarterlyGoal(id, updates)
        if (result.error) throw result.error
        data = result.data
      }

      return {
        data,
        weeklyGoals,
        originalGoal,
        type: isReschedule ? 'reschedule' : 'update'
      }
    } catch (error) {
      return rejectWithValue({
        message: error.message,
        id,
        originalGoal,
        type: isReschedule ? 'reschedule' : 'update'
      })
    }
  }
)

export const deleteQuarterlyGoal = createAsyncThunk(
  'goals/deleteQuarterlyGoal',
  async (id, { rejectWithValue, getState }) => {
    const state = getState()
    const goalToDelete = state.goals.quarterlyGoals.find(goal => goal.id === id)
    
    try {
      const { error } = await goalsAPI.deleteQuarterlyGoal(id)
      if (error) throw error
      return { 
        id, 
        deletedGoal: goalToDelete,
        type: 'delete'
      }
    } catch (error) {
      return rejectWithValue({ 
        message: error.message, 
        id,
        deletedGoal: goalToDelete,
        type: 'delete'
      })
    }
  }
)

// Quarterly Goals Reducers with Optimistic Updates
export const quarterlyGoalsReducers = {
  // Fetch Quarterly Goals
  [fetchQuarterlyGoals.pending]: (state) => {
    state.loading.quarterly = true
    state.error.quarterly = null
  },
  [fetchQuarterlyGoals.fulfilled]: (state, action) => {
    state.loading.quarterly = false
    state.quarterlyGoals = action.payload
    state.initialized.quarterly = true
  },
  [fetchQuarterlyGoals.rejected]: (state, action) => {
    state.loading.quarterly = false
    state.error.quarterly = action.payload
  },

  // Create Quarterly Goal - Optimistic
  [createQuarterlyGoal.pending]: (state, action) => {
    const { userId, goalData } = action.meta.arg
    const tempId = generateTempId()
    
    const optimisticGoal = {
      ...goalData,
      id: tempId,
      user_id: userId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      status: goalData.status || 'draft',
      isOptimistic: true,
      isLoading: true
    }
    
    state.quarterlyGoals.unshift(optimisticGoal)
    state.error.quarterly = null
  },
  [createQuarterlyGoal.fulfilled]: (state, action) => {
    const { data } = action.payload
    const tempIndex = state.quarterlyGoals.findIndex(goal => goal.isOptimistic && goal.isLoading)
    
    if (tempIndex !== -1) {
      // Replace optimistic goal with real data
      state.quarterlyGoals[tempIndex] = {
        ...data,
        isOptimistic: false,
        isLoading: false
      }
    } else {
      // Fallback: just add the new goal
      state.quarterlyGoals.unshift(data)
    }
  },
  [createQuarterlyGoal.rejected]: (state, action) => {
    // Remove the optimistic goal
    state.quarterlyGoals = state.quarterlyGoals.filter(goal => 
      !goal.isOptimistic || !goal.isLoading
    )
    state.error.quarterly = action.payload.message
  },

  // Update Quarterly Goal - Optimistic
  [updateQuarterlyGoal.pending]: (state, action) => {
    const { id, ...updates } = action.meta.arg
    const index = state.quarterlyGoals.findIndex(goal => goal.id === id)
    
    if (index !== -1) {
      // Apply optimistic update
      state.quarterlyGoals[index] = {
        ...state.quarterlyGoals[index],
        ...updates,
        updated_at: new Date().toISOString(),
        isOptimistic: true,
        isLoading: true
      }
    }
    state.error.quarterly = null
  },
  [updateQuarterlyGoal.fulfilled]: (state, action) => {
    const { data, weeklyGoals, type } = action.payload
    const index = state.quarterlyGoals.findIndex(goal => goal.id === data.id)

    if (index !== -1) {
      state.quarterlyGoals[index] = {
        ...data,
        isOptimistic: false,
        isLoading: false
      }
    }

    // If this was a reschedule operation, also update the cascaded weekly goals
    if (type === 'reschedule' && weeklyGoals?.length > 0) {
      weeklyGoals.forEach(updatedWeekly => {
        const weeklyIndex = state.weeklyGoals.findIndex(g => g.id === updatedWeekly.id)
        if (weeklyIndex !== -1) {
          state.weeklyGoals[weeklyIndex] = {
            ...state.weeklyGoals[weeklyIndex],
            ...updatedWeekly
          }
        }
      })
    }
  },
  [updateQuarterlyGoal.rejected]: (state, action) => {
    const { originalGoal, id } = action.payload
    const index = state.quarterlyGoals.findIndex(goal => goal.id === id)
    
    if (index !== -1 && originalGoal) {
      // Rollback to original state
      state.quarterlyGoals[index] = {
        ...originalGoal,
        isOptimistic: false,
        isLoading: false
      }
    }
    state.error.quarterly = action.payload.message
  },

  // Delete Quarterly Goal - Optimistic
  [deleteQuarterlyGoal.pending]: (state, action) => {
    const id = action.meta.arg
    const index = state.quarterlyGoals.findIndex(goal => goal.id === id)
    
    if (index !== -1) {
      // Mark as deleting instead of removing immediately
      state.quarterlyGoals[index] = {
        ...state.quarterlyGoals[index],
        isOptimistic: true,
        isLoading: true,
        isDeleting: true
      }
    }
    state.error.quarterly = null
  },
  [deleteQuarterlyGoal.fulfilled]: (state, action) => {
    const { id } = action.payload
    // Permanently remove the goal
    state.quarterlyGoals = state.quarterlyGoals.filter(goal => goal.id !== id)
  },
  [deleteQuarterlyGoal.rejected]: (state, action) => {
    const { id, deletedGoal } = action.payload
    const index = state.quarterlyGoals.findIndex(goal => goal.id === id)
    
    if (index !== -1) {
      // Restore the goal (remove deleting state)
      state.quarterlyGoals[index] = {
        ...deletedGoal,
        isOptimistic: false,
        isLoading: false,
        isDeleting: false
      }
    } else if (deletedGoal) {
      // If somehow removed, add it back
      state.quarterlyGoals.push({
        ...deletedGoal,
        isOptimistic: false,
        isLoading: false,
        isDeleting: false
      })
    }
    state.error.quarterly = action.payload.message
  }
}
